import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import ButtonOne from '../../../common/normal-button';
import {
  CTWA_ACCOUNT_CHECKS, CTWA_DRAFTS, CTWA_DRAFT_DETAIL, CTWA_ESTIMATE, CTWA_PUBLISH,
} from '../../../config/urls';
import { ColorsConstants } from '../../../cssUtils/constants';
import { needsUpload, uploadAdMedia, useCtwaApi } from './api';
import { DRAFT_VERSION, GOALS, SECTIONS, emptyAd, initialForm } from './constants';
import { buildPayload, validateAll, validateSection } from './validation';
import { Section, currencySymbol } from './ui';
import AccountSetupSection from './AccountSetupSection';
import { ObjectiveSection, PlacementsSection } from './CampaignSections';
import AdsSection from './AdsSection';
import TargetingSection, { AudiencePanel } from './TargetingSection';
import BudgetSection from './BudgetSection';
import { AutofillSection } from './ChatSections';
import ReviewScreen from './ReviewScreen';

// Local-only fields (File objects, blob URLs) can't go into a draft; uploaded media is kept by its asset id.
const stripForDraft = (form) => ({
  ...form,
  ads: form.ads.map((ad) => ({
    ...ad,
    file: null,
    previewUrl: '',
    cards: ad.cards.map((c) => ({ ...c, file: null, previewUrl: '' })),
  })),
});

const AdPreview = ({ ad, pageName }) => {
  const image = ad?.format === 'CAROUSEL' ? ad.cards[0]?.previewUrl : ad?.format === 'IMAGE' ? ad.previewUrl : '';
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, backgroundColor: ColorsConstants.primaryBlue60 }}>
        <Typography variant="body2" fontWeight={700}>Preview · {ad?.name}</Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>{pageName || 'Your Page'}</Typography>
        <Typography variant="caption" color={ColorsConstants.primaryGray50} sx={{ display: 'block', mb: 1 }}>Sponsored</Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-wrap' }}>{ad?.primaryText || '[Primary text appears here]'}</Typography>
        <Box sx={{
          aspectRatio: (ad?.ratio || ad?.cards?.[0]?.ratio || '4:5').replace(':', '/'), maxHeight: 320, borderRadius: 1,
          background: image ? `url(${image}) center/cover` : '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {!image && <Typography variant="caption" color={ColorsConstants.primaryGray50}>{ad?.format === 'VIDEO' ? '[Video]' : '[Ad image]'}</Typography>}
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 10, color: ColorsConstants.primaryGray50 }}>WHATSAPP</Typography>
            <Typography variant="caption" fontWeight={700}>{ad?.headline || '[Headline]'}</Typography>
          </Box>
          <Box sx={{ backgroundColor: '#15803D', color: 'white', borderRadius: 1, px: 1, py: 0.5, fontSize: 11, fontWeight: 700 }}>Send message</Box>
        </Stack>
      </Box>
    </Paper>
  );
};

const CampaignCreate = ({ onBack, onPublished, config, onConfigRefresh, draftId = null, canPublish = true }) => {
  const api = useCtwaApi();

  const [form, setForm] = useState(() => {
    const f = initialForm();
    f.account = {
      adAccountId: config?.ad_account_id || '', adAccountName: config?.ad_account_name || '',
      pageId: config?.fb_page_id || '', pageName: config?.fb_page_name || '',
      waNumberId: config?.wa_number_id || '', waNumber: config?.wa_number || '', waNumberName: config?.wa_number_name || '',
    };
    return f;
  });
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const patchNested = (key) => (p) => setForm((f) => ({ ...f, [key]: { ...f[key], ...p } }));
  const patchAd = (adKey, p) => setForm((f) => ({ ...f, ads: f.ads.map((a) => (a.key === adKey ? { ...a, ...p } : a)) }));

  const [openId, setOpenId] = useState('account');
  const [doneIds, setDoneIds] = useState(new Set());
  const [view, setView] = useState('edit');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [activeDraftId, setActiveDraftId] = useState(draftId);
  const [busy, setBusy] = useState(''); // 'draft' | 'review' | 'publish'

  // ── Load draft ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!draftId) return;
    api.get(CTWA_DRAFT_DETAIL(draftId)).then(({ name, form_data: fd }) => {
      if (fd?.v === DRAFT_VERSION) {
        // Fields added since the draft was saved (e.g. chatbot) get their defaults.
        setForm({ ...initialForm(), ...fd, ads: fd.ads.map((a) => ({ ...emptyAd(), ...a })) });
        setDoneIds(new Set(SECTIONS.map((s) => s.id)));
      } else {
        patch({ name: name || '' });
        setNotice('This draft was made with the older campaign form, so only its name was kept.');
      }
    }).catch((e) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  // ── Account pre-checks (re-run whenever the selection changes) ──────────────
  const [checks, setChecks] = useState(null);
  const [checksLoading, setChecksLoading] = useState(false);
  const runChecks = async () => {
    if (!form.account.adAccountId) return;
    setChecksLoading(true);
    try {
      const qs = new URLSearchParams({ ad_account_id: form.account.adAccountId, page_id: form.account.pageId, wa_number: form.account.waNumber });
      setChecks(await api.get(`${CTWA_ACCOUNT_CHECKS}?${qs}`));
    } catch (e) {
      setChecks({ can_continue: false, checks: [{ key: 'checks', label: 'Pre-checks', status: 'unknown', blocking: true, message: e.message }] });
    } finally {
      setChecksLoading(false);
    }
  };
  useEffect(() => { runChecks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.account.adAccountId, form.account.pageId, form.account.waNumber]);

  const accountInfo = checks?.account;
  const ctx = {
    checks,
    minDailyBudget: accountInfo?.min_daily_budget ? Math.ceil(accountInfo.min_daily_budget / 100) : null,
    currencySymbol: currencySymbol(accountInfo?.currency || 'INR'),
  };

  // ── Meta estimate: audience size, conversations/day, retired interests ──────
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const estimateBody = JSON.stringify({
    ad_account_id: form.account.adAccountId,
    goal: form.goal,
    special_ad_category: form.specialAdCategory,
    daily_budget: Number(form.budget.daily) || null,
    placements: form.placementMode === 'manual' ? form.placements : [],
    targeting: buildPayload(form).targeting,
  });
  const estimateSeq = useRef(0);
  useEffect(() => {
    if (!form.account.adAccountId || form.targeting.locations.length === 0) { setEstimate(null); return undefined; }
    const seq = ++estimateSeq.current;
    setEstimateLoading(true);
    const timer = setTimeout(() => {
      api.post(CTWA_ESTIMATE, JSON.parse(estimateBody))
        .then((d) => { if (seq === estimateSeq.current) { setEstimate(d); setEstimateError(d.error || ''); } })
        .catch((e) => { if (seq === estimateSeq.current) setEstimateError(e.message); })
        .finally(() => { if (seq === estimateSeq.current) setEstimateLoading(false); });
    }, 600);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateBody]);
  const retiredIds = estimate?.retired_ids || [];
  const retiredNames = form.targeting.detailed.filter((d) => retiredIds.includes(d.id)).map((d) => d.name);

  // ── Sections ────────────────────────────────────────────────────────────────
  const errorsFor = (id) => validateSection(id, form, ctx);
  const markDone = (id) => {
    setDoneIds((s) => new Set([...s, id]));
    if (errorsFor(id).length) return; // stay open so the errors are visible
    const idx = SECTIONS.findIndex((s) => s.id === id);
    setOpenId(SECTIONS[idx + 1]?.id || null);
  };

  const sectionProps = (id, num) => ({
    num,
    title: SECTIONS.find((s) => s.id === id).title,
    open: openId === id,
    onToggle: () => setOpenId(openId === id ? null : id),
    isDone: doneIds.has(id),
    showErrors: doneIds.has(id),
    errors: errorsFor(id),
    onDone: () => markDone(id),
  });

  // ── Media upload (before review and before saving a draft) ──────────────────
  const uploadPendingMedia = async () => {
    let current = form;
    for (const ad of form.ads) {
      if (!needsUpload(ad)) continue;
      const id = await uploadAdMedia(api, form.account.adAccountId, ad);
      current = { ...current, ads: current.ads.map((a) => (a.key === ad.key ? { ...a, mediaAssetId: id } : a)) };
      setForm(current);
    }
    return current;
  };

  const saveDraft = async () => {
    if (!form.name.trim()) { setError('Enter a campaign name before saving a draft.'); return; }
    setBusy('draft');
    setError('');
    try {
      let current = form;
      try { current = await uploadPendingMedia(); } catch (e) { setNotice(`Saved without some media: ${e.message}`); }
      const body = { name: current.name.trim(), ad_account_id: current.account.adAccountId || null, fb_page_id: current.account.pageId || null, form_data: stripForDraft(current) };
      if (activeDraftId) await api.put(CTWA_DRAFT_DETAIL(activeDraftId), body);
      else setActiveDraftId((await api.post(CTWA_DRAFTS, body)).id);
      setNotice('Draft saved. You can continue it later from the Campaigns list.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const goToReview = async () => {
    setDoneIds(new Set(SECTIONS.map((s) => s.id)));
    setError('');
    setBusy('review');
    try {
      // Upload only when the ads are otherwise valid; a bad file would just fail on the server too.
      if (!validateSection('ads', form, ctx).length && form.account.adAccountId) await uploadPendingMedia();
      setView('review');
    } catch (e) {
      setError(`Media upload failed: ${e.message}`);
      setOpenId('ads');
    } finally {
      setBusy('');
    }
  };

  const publish = async () => {
    setBusy('publish');
    setError('');
    try {
      const res = await api.post(CTWA_PUBLISH, buildPayload(form, { retiredIds, draftId: activeDraftId }));
      if (activeDraftId) api.del(CTWA_DRAFT_DETAIL(activeDraftId)).catch(() => {});
      onPublished(res.campaign_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const localErrors = useMemo(() => validateAll(form, ctx), // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, checks]);

  const [previewKey, setPreviewKey] = useState(null);
  const previewAd = form.ads.find((a) => a.key === previewKey) || form.ads[0];

  if (view === 'review') {
    return (
      <ReviewScreen
        form={form}
        payload={buildPayload(form, { retiredIds, draftId: activeDraftId })}
        localErrors={localErrors}
        retiredNames={retiredNames}
        accountInfo={accountInfo}
        igConnected={!!config?.ig_connected}
        onFix={(id) => { setView('edit'); setOpenId(id); }}
        onBack={() => setView('edit')}
        onPublish={publish}
        onSaveDraft={saveDraft}
        publishing={busy === 'publish'}
        savingDraft={busy === 'draft'}
        publishError={error}
        canPublish={canPublish}
      />
    );
  }

  const t = form.targeting;
  const sym = ctx.currencySymbol;
  const summaries = {
    account: [form.account.adAccountName, form.account.pageName, form.account.waNumber && `+${form.account.waNumber}`].filter(Boolean).join(' · '),
    objective: GOALS.find((g) => g.value === form.goal)?.label,
    placements: form.placementMode === 'automatic' ? 'Automatic' : `${form.placements.length} selected`,
    ads: `${form.ads.length} ads in this ad set`,
    targeting: `${t.locations.map((l) => l.name).join(', ') || 'No location'} · Advantage+ ${t.advantageAudience ? 'on' : 'off'}`,
    budget: form.budget.daily ? `${sym}${form.budget.daily}/day · ${form.budget.endMode === 'date' ? `until ${form.budget.endDate}` : 'no end date'}` : '',
    autofill: `${form.autofillMode === 'all' ? form.autofillAll : 'Autofill set per ad'} · ${form.chatbotMode === 'per_ad' ? 'chatbot set per ad' : form.chatbotAll ? 'chatbot linked' : 'no chatbot'}`,
  };
  const num = (id) => SECTIONS.findIndex((s) => s.id === id);

  return (
    <Box sx={{ p: 3, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Stack direction="row" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} component="button" type="button" onClick={onBack}
          sx={{ cursor: 'pointer', border: 0, background: 'none', font: 'inherit', p: 0 }}>
          <ArrowBack fontSize="small" sx={{ color: ColorsConstants.primaryGray50 }} />
          <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray50}>Create Ad Campaign</Typography>
        </Stack>
        <TextField size="small" placeholder="Campaign name *" value={form.name} onChange={(e) => patch({ name: e.target.value })}
          inputProps={{ 'aria-label': 'Campaign name' }} sx={{ flex: 1, maxWidth: 340 }} />
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <ButtonOne title={busy === 'draft' ? 'Saving…' : 'Save as Draft'} variant="outlined" onClick={saveDraft} disabled={!!busy}
            startIcon={busy === 'draft' && <CircularProgress size={16} color="inherit" />} />
          <ButtonOne title={busy === 'review' ? 'Uploading media…' : 'Review & Publish'} onClick={goToReview} disabled={!!busy || !form.name.trim()}
            startIcon={busy === 'review' && <CircularProgress size={16} color="inherit" />} />
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Section {...sectionProps('account', num('account'))} summary={summaries.account}>
            <AccountSetupSection
              account={form.account} onChange={patchNested('account')} config={config} onConfigRefresh={onConfigRefresh}
              checks={checks} checksLoading={checksLoading} onRecheck={runChecks}
            />
          </Section>
          <Section {...sectionProps('objective', num('objective'))} summary={summaries.objective}>
            <ObjectiveSection goal={form.goal} specialAdCategory={form.specialAdCategory} onChange={patch} />
          </Section>
          <Section {...sectionProps('placements', num('placements'))} summary={summaries.placements}>
            <PlacementsSection mode={form.placementMode} selected={form.placements} igConnected={!!config?.ig_connected} onChange={patch} />
          </Section>
          <Section {...sectionProps('ads', num('ads'))} summary={summaries.ads}>
            <AdsSection ads={form.ads} onChange={(ads) => patch({ ads })} showErrors={doneIds.has('ads')} />
          </Section>
          <Section {...sectionProps('targeting', num('targeting'))} summary={summaries.targeting}>
            <TargetingSection targeting={t} specialAdCategory={form.specialAdCategory} retiredIds={retiredIds} onChange={patchNested('targeting')} />
          </Section>
          <Section {...sectionProps('budget', num('budget'))} summary={summaries.budget}>
            <BudgetSection budget={form.budget} onChange={patchNested('budget')} accountInfo={accountInfo} estimate={estimate} />
          </Section>
          <Section {...sectionProps('autofill', num('autofill'))} summary={summaries.autofill}>
            <AutofillSection form={form} onChange={patch} onAdChange={patchAd} />
          </Section>
        </Box>

        <Stack spacing={2} sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0, position: { lg: 'sticky' }, top: 24 }}>
          {openId === 'targeting' || openId === 'budget' ? (
            <AudiencePanel estimate={estimate} loading={estimateLoading} error={estimateError} targeting={t} specialAdCategory={form.specialAdCategory} />
          ) : (
            <>
              <Stack direction="row" gap={0.5} flexWrap="wrap">
                {form.ads.map((a, i) => (
                  <ButtonOne key={a.key} title={`Ad ${i + 1}`} variant={a.key === previewAd?.key ? 'contained' : 'outlined'}
                    onClick={() => setPreviewKey(a.key)} sx={{ minWidth: 0, px: 1.5, height: 32 }} />
                ))}
              </Stack>
              <AdPreview ad={previewAd} pageName={form.account.pageName} />
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CampaignCreate;
